/**
 * İstek kısıtlama katmanı — axios ADAPTER'ına takılır.
 *
 * ── SORUN ───────────────────────────────────────────────────────────────────
 * `/company-list` açılışında konsolda görülen istekler:
 *
 *   GET /events/connect-active
 *   GET /events/{id}/participation-status      ← İKİ KEZ
 *   GET /businesses?mine=true&page=1&limit=100 ← İKİ KEZ
 *   GET /businesses?mine=true&page=2&limit=100
 *   GET /businesses?scope=connect&page=1&limit=100
 *   GET /users/directory/{id}
 *   ...
 *
 * Tekrarlar tesadüf değil: `participation-status`ü hem `jsx/index.tsx` hem
 * `useMenuFilter` çağırıyor ve bu BİLEREK yapılmış (bkz. index.tsx'teki
 * kilitlenme açıklaması). Yani çağrı yerlerini tek tek budamak o mantığı
 * bozar. Doğru katman aşağısı: aynı anda uçan aynı istek TEK ağ isteğine
 * indirgenir, çağıranlar değişmeden kalır.
 *
 * Ayrıca API HTTP/2 konuşuyor (`listen 443 ssl http2`), yani tarayıcının
 * HTTP/1.1'deki "host başına 6 bağlantı" freni BURADA YOK — onlarca istek
 * gerçekten aynı anda çıkar ve edge nginx'in `limit_req zone=api` eşiğini
 * tetikler. Eş zamanlılık tavanı o patlamayı düzleştirir.
 *
 * ── NEDEN ADAPTER, INTERCEPTOR DEĞİL ────────────────────────────────────────
 * 🔴 Eş zamanlılık sayacı request interceptor'da alınıp response
 *    interceptor'da bırakılsaydı, araya giren bir `reject` (ör. oturum
 *    süresi dolduğunda `performLogout` sonrası atılan hata) slotu SIZDIRIRDI.
 *    Yeterince sızıntıda uygulama tamamen kilitlenir. Adapter tek bir
 *    fonksiyon; `then(release, release)` ile her iki sonuçta da bırakılır.
 *
 * ⚠️ `axiosInstance.request`i sarmalamak ÇALIŞMAZ: axios `get/post/...`
 *    metotlarını iç `Axios` bağlamına bağlar, dışarıdan atanan `request`
 *    görülmez.
 */

type AnyConfig = Record<string, any>;
type Adapter = (config: AnyConfig) => Promise<any>;

/** Aynı anda açık tutulacak en fazla ağ isteği. */
export const DEFAULT_MAX_CONCURRENT = 6;

/**
 * Sayaçlı geçiş kapısı.
 *
 * 🔴 `release` içinde slot BEKLEYENE DEVREDİLİR, `active` azaltılmaz. Hem
 *    azaltıp hem sıradakini uyandırmak aynı slotu iki isteğe verirdi ve
 *    tavan sessizce delinirdi.
 */
export function createSemaphore(maxConcurrent: number) {
  let active = 0;
  const waiting: Array<() => void> = [];

  const acquire = (): Promise<void> => {
    if (active < maxConcurrent) {
      active += 1;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      waiting.push(resolve);
    });
  };

  const release = () => {
    const next = waiting.shift();
    if (next) {
      next();
      return;
    }
    active = Math.max(0, active - 1);
  };

  return {
    run<T>(task: () => Promise<T>): Promise<T> {
      return acquire().then(() => {
        const started = task();
        // Slot her iki sonuçta da bırakılır — sızıntı yolu yok.
        started.then(release, release);
        return started;
      });
    },
    get activeCount() {
      return active;
    },
    get waitingCount() {
      return waiting.length;
    },
  };
}

/** Yalnızca okuma istekleri teklenebilir. */
const DEDUPABLE_METHODS = new Set(['get', 'head']);

function readHeader(config: AnyConfig, name: string): string {
  const headers = config?.headers;
  if (!headers) return '';
  if (typeof headers.get === 'function') return String(headers.get(name) ?? '');
  return String(headers[name] ?? headers[name.toLowerCase()] ?? '');
}

/**
 * Teklenebilir mi? Değilse `null`.
 *
 * 🔴 POST/PATCH/DELETE ASLA teklenmez. İki ayrı "teklif oluştur" çağrısını
 *    birleştirmek bir kaydı sessizce yutardı.
 */
export function dedupeKey(config: AnyConfig): string | null {
  const method = String(config?.method ?? 'get').toLowerCase();
  if (!DEDUPABLE_METHODS.has(method)) return null;

  // ⚠️ İptal edilebilir istek PAYLAŞILMAZ: bir çağıran iptal ederse
  //    diğerlerinin isteği de düşerdi.
  if (config?.signal || config?.cancelToken) return null;

  // ⚠️ Akış/ikili yanıtlar PAYLAŞILMAZ — gövde bir kez tüketilebilir.
  const responseType = config?.responseType;
  if (responseType && responseType !== 'json' && responseType !== 'text') return null;

  // Çağıran açıkça "tekleme" diyebilsin (ör. bilinçli yeniden doğrulama).
  if (config?.noDedupe) return null;

  let params: string;
  try {
    params = JSON.stringify(config?.params ?? null);
  } catch {
    // Serileştirilemeyen parametre → güvenli taraf: tekleme.
    return null;
  }

  // ⚠️ Token anahtara DAHİL. Yenileme sonrası farklı kimlikle giden bir
  //    istek, eski kimliğin yanıtını almamalı.
  const auth = readHeader(config, 'authorization');
  return `${method} ${config?.baseURL ?? ''}${config?.url ?? ''}?${params}#${auth}`;
}

/**
 * Hatayı çağıranın KENDİ config'iyle klonlar.
 *
 * ⚠️ Tek hata nesnesi paylaşılsaydı, response interceptor'ın 401 yeniden
 *    deneme işareti (`_retried`) bir çağırandan diğerine sızardı: ikincisi
 *    hiç denememiş olmasına rağmen "zaten denendi" sayılırdı.
 */
function withConfig(error: any, config: AnyConfig) {
  if (!error || typeof error !== 'object') return error;

  // 🔴 `Object.assign` BURADA YETMEZ. `Error` uzerinde `message` ve `stack`
  //    own ama NUMARALANAMAZ (non-enumerable) ozelliklerdir; `assign`
  //    yalnizca numaralanabilir olanlari kopyalar. Sonuc: klonlanan hatanin
  //    mesaji BOS kalir ve kullaniciya "Sunucuya ulasilamadi" yerine bos bir
  //    metin gider. Testle yakalandi — tanimlayicilarin TAMAMI kopyalaniyor.
  const clone = Object.create(
    Object.getPrototypeOf(error),
    Object.getOwnPropertyDescriptors(error),
  );

  // 🔴 `stack` (ve bazi motorlarda `message`) ERISIMCI olabilir ve getter'i
  //    ORIJINAL nesnenin ic yuvasina bagimlidir — klonda cagrilinca
  //    `undefined` doner. Testle yakalandi. Degerleri okuyup DUZ VERI olarak
  //    yaziyoruz; hata ayiklamada yigin izi kaybolmasin.
  (['message', 'stack'] as const).forEach((prop) => {
    const value = (error as any)[prop];
    if (value === undefined) return;
    Object.defineProperty(clone, prop, {
      value,
      writable: true,
      enumerable: false,
      configurable: true,
    });
  });

  Object.defineProperty(clone, 'config', {
    value: config,
    writable: true,
    enumerable: true,
    configurable: true,
  });
  return clone;
}

/**
 * @param baseAdapter axios'un gerçek adapter'ı (`axios.getAdapter(...)`).
 */
export function createThrottledAdapter(
  baseAdapter: Adapter,
  options: { maxConcurrent?: number } = {},
): Adapter {
  const semaphore = createSemaphore(options.maxConcurrent ?? DEFAULT_MAX_CONCURRENT);
  const inflight = new Map<string, Promise<any>>();

  return function throttledAdapter(config: AnyConfig) {
    const key = dedupeKey(config);
    if (key === null) return semaphore.run(() => baseAdapter(config));

    let shared = inflight.get(key);
    if (!shared) {
      shared = semaphore.run(() => baseAdapter(config));
      const clear = () => {
        // ⚠️ Kimlik kontrolü şart: bu arada aynı anahtarla YENİ bir istek
        //    başlamış olabilir, onu silmemeliyiz.
        if (inflight.get(key) === shared) inflight.delete(key);
      };
      shared.then(clear, clear);
      inflight.set(key, shared);
    }

    return shared.then(
      // ⚠️ `data` çağıranlar arasında PAYLAŞILIR (aynı referans). Yanıtı
      //    yerinde değiştiren kod diğerlerini de etkiler — bu kod tabanında
      //    yanıtlar salt okunur kullanılıyor.
      (response) => ({ ...response, config }),
      (error) => {
        throw withConfig(error, config);
      },
    );
  };
}
